import React, { useState, useEffect } from 'react';
import { Box, Grid, Card, CardContent, Alert, Skeleton } from '@mui/material';
import { WalteroCard, WalteroTypography, WalteroButton } from '../waltero';
import { subscriptionService } from '../../services/subscriptionService';

const SubscriptionOverview = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    totalSubscriptionTypes: 0,
    totalOrganizationSubscriptions: 0,
    totalSensorAssignments: 0,
    activeSubscriptions: 0
  });
  const [subscriptionTypes, setSubscriptionTypes] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);

  useEffect(() => {
    loadOverviewData();
  }, []);

  const loadOverviewData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [typesResult, orgSubsResult, sensorSubsResult] = await Promise.all([
        subscriptionService.getSubscriptionTypes(),
        subscriptionService.getAllOrganizationSubscriptions(),
        subscriptionService.getAllSensorSubscriptions()
      ]);

      // Calculate statistics
      const stats = {
        totalSubscriptionTypes: typesResult.data?.length || 0,
        totalOrganizationSubscriptions: orgSubsResult.data?.length || 0,
        totalSensorAssignments: sensorSubsResult.data?.length || 0,
        activeSubscriptions: orgSubsResult.data?.filter(sub => sub.status === 'active').length || 0
      };

      setStats(stats);
      setSubscriptionTypes(typesResult.data || []);

      // Create recent activity from organization subscriptions (most recent first)
      const sortedOrgSubs = (orgSubsResult.data || [])
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
        .slice(0, 5);

      setRecentActivity(sortedOrgSubs);

    } catch (err) {
      console.error('Error loading overview data:', err);
      setError(err.message || 'Failed to load overview data');
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, description, color = 'primary' }) => (
    <WalteroCard sx={{ height: '100%' }}>
      <CardContent sx={{ p: 3, textAlign: 'center' }}>
        <WalteroTypography 
          variant="h3" 
          sx={{ 
            color: `waltero.${color}.coreBlue`,
            fontWeight: 700,
            mb: 1 
          }}
        >
          {loading ? <Skeleton width={60} /> : value}
        </WalteroTypography>
        <WalteroTypography 
          variant="h6" 
          sx={{ 
            color: 'text.primary',
            fontWeight: 600,
            mb: 1 
          }}
        >
          {title}
        </WalteroTypography>
        <WalteroTypography 
          variant="body2" 
          sx={{ color: 'text.secondary' }}
        >
          {description}
        </WalteroTypography>
      </CardContent>
    </WalteroCard>
  );

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        <strong>Error Loading Overview</strong>
        <br />
        {error}
        <Box sx={{ mt: 2 }}>
          <WalteroButton 
            variant="outlined" 
            onClick={loadOverviewData}
            size="small"
          >
            RETRY
          </WalteroButton>
        </Box>
      </Alert>
    );
  }

  return (
    <Box>
      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Subscription Types"
            value={stats.totalSubscriptionTypes}
            description="Global subscription templates"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Active Subscriptions"
            value={stats.activeSubscriptions}
            description="Organization subscriptions"
            color="secondary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Org Subscriptions"
            value={stats.totalOrganizationSubscriptions}
            description="All org assignments"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Sensor Assignments"
            value={stats.totalSensorAssignments}
            description="Individual sensor subscriptions"
          />
        </Grid>
      </Grid>

      {/* Content Grid */}
      <Grid container spacing={3}>
        {/* Available Subscription Types */}
        <Grid item xs={12} md={6}>
          <WalteroCard>
            <Box sx={{ p: 3 }}>
              <WalteroTypography 
                variant="h6" 
                sx={{ 
                  mb: 2,
                  color: 'waltero.primary.coreBlue',
                  fontWeight: 600 
                }}
              >
                Available Subscription Types
              </WalteroTypography>
              
              {loading ? (
                <Box>
                  {[1, 2, 3].map(i => (
                    <Skeleton key={i} height={60} sx={{ mb: 1 }} />
                  ))}
                </Box>
              ) : subscriptionTypes.length === 0 ? (
                <Alert severity="info">
                  No subscription types found. Create your first subscription type to get started.
                </Alert>
              ) : (
                <Box>
                  {subscriptionTypes.map((type) => (
                    <Card 
                      key={type.id} 
                      variant="outlined" 
                      sx={{ 
                        mb: 2, 
                        '&:last-child': { mb: 0 },
                        backgroundColor: type.isActive ? 'background.paper' : 'action.hover'
                      }}
                    >
                      <CardContent sx={{ p: 2 }}>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Box>
                            <WalteroTypography 
                              variant="subtitle1" 
                              sx={{ fontWeight: 600 }}
                            >
                              {type.name}
                            </WalteroTypography>
                            <WalteroTypography 
                              variant="body2" 
                              sx={{ color: 'text.secondary' }}
                            >
                              {type.durationDays} days • Max {type.maxSensors === -1 ? 'Unlimited' : type.maxSensors} sensors
                            </WalteroTypography>
                            {type.basePrice && (
                              <WalteroTypography 
                                variant="body2" 
                                sx={{ color: 'waltero.primary.coreBlue', fontWeight: 600 }}
                              >
                                ${type.basePrice}
                              </WalteroTypography>
                            )}
                          </Box>
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              backgroundColor: type.isActive ? 'success.main' : 'error.main'
                            }}
                          />
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              )}
            </Box>
          </WalteroCard>
        </Grid>

        {/* Recent Activity */}
        <Grid item xs={12} md={6}>
          <WalteroCard>
            <Box sx={{ p: 3 }}>
              <WalteroTypography 
                variant="h6" 
                sx={{ 
                  mb: 2,
                  color: 'waltero.primary.coreBlue',
                  fontWeight: 600 
                }}
              >
                Recent Subscription Activity
              </WalteroTypography>
              
              {loading ? (
                <Box>
                  {[1, 2, 3, 4, 5].map(i => (
                    <Skeleton key={i} height={50} sx={{ mb: 1 }} />
                  ))}
                </Box>
              ) : recentActivity.length === 0 ? (
                <Alert severity="info">
                  No recent subscription activity found.
                </Alert>
              ) : (
                <Box>
                  {recentActivity.map((activity) => (
                    <Box 
                      key={activity.id}
                      sx={{ 
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        py: 2,
                        borderBottom: 1,
                        borderColor: 'divider',
                        '&:last-child': { borderBottom: 0 }
                      }}
                    >
                      <Box>
                        <WalteroTypography 
                          variant="body2" 
                          sx={{ fontWeight: 600 }}
                        >
                          Organization Subscription
                        </WalteroTypography>
                        <WalteroTypography 
                          variant="body2" 
                          sx={{ color: 'text.secondary' }}
                        >
                          {activity.customName || 'Subscription assigned'}
                        </WalteroTypography>
                        <WalteroTypography 
                          variant="caption" 
                          sx={{ color: 'text.secondary' }}
                        >
                          {new Date(activity.createdAt).toLocaleDateString()}
                        </WalteroTypography>
                      </Box>
                      <Box
                        sx={{
                          px: 2,
                          py: 0.5,
                          borderRadius: 1,
                          backgroundColor: activity.status === 'active' ? 'success.light' : 'warning.light',
                          color: activity.status === 'active' ? 'success.dark' : 'warning.dark'
                        }}
                      >
                        <WalteroTypography variant="caption" sx={{ textTransform: 'uppercase' }}>
                          {activity.status}
                        </WalteroTypography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          </WalteroCard>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SubscriptionOverview;